import { TERMINAL_STAGES } from './store';
import { Investment, InvestmentStage, InvestmentStageType } from './types';

export type InvestorTimelineState = 'done' | 'current' | 'upcoming';

export interface InvestorTimelineEntry {
  key: string;
  label: string;
  state: InvestorTimelineState;
  at: string | null;
}

interface InvestorTimelineStep {
  key: string;
  label: string;
  stages: InvestmentStageType[];
}

/**
 * The investor-facing step sequence: a curated regrouping of `InvestmentStage`
 * that never surfaces internal-only stages (`LinkClicked`, `DueDiligenceViewed`)
 * or terminal/off-path stages (`Rejected`, `NotEligible`, `Expired`, `Redeemed`)
 * — those are represented by the status badge, not as a timeline step. Order
 * mirrors the canonical forward path through `LEGAL_TRANSITIONS` in
 * `state-machine.ts`.
 *
 * The first four steps carry the wizard's own names, so the reader sees the
 * same four words here and in the wizard. That is why signing and consenting
 * share one step: the wizard asks for both under "Sign & Consent", and a
 * timeline that split them would describe a flow the investor never saw.
 * `PaymentMethodSelected` is likewise folded into Payment. The two steps after
 * Payment are the fund's, and stay separate because the investor waits on each.
 */
const INVESTOR_TIMELINE_STEPS: InvestorTimelineStep[] = [
  { key: 'amount', label: 'Amount', stages: [InvestmentStage.SubscriptionStarted] },
  { key: 'eligibility', label: 'Eligibility', stages: [InvestmentStage.EligibilitySubmitted] },
  {
    key: 'sign-and-consent',
    label: 'Sign & Consent',
    stages: [InvestmentStage.AgreementSigned, InvestmentStage.DataSubmissionAcknowledged],
  },
  { key: 'payment', label: 'Payment', stages: [InvestmentStage.FundsSent] },
  { key: 'funds-received', label: 'Funds received', stages: [InvestmentStage.FundsReceived] },
  { key: 'units-issued', label: 'Units issued', stages: [InvestmentStage.Invested] },
];

/**
 * Derives the investor-facing progress timeline for an investment. A step is
 * `done` once `stageTimeline` records every stage it groups, and is dated from
 * the last of them; the first non-done step is `current` while the investment
 * is still active (its live `stage` is not in `TERMINAL_STAGES`); everything
 * else is `upcoming`. A terminal investment (e.g. withdrawn/`Rejected`) has no
 * `current` entry — steps it never reached simply stay `upcoming`.
 */
export const getInvestorTimeline = (investment: Investment): InvestorTimelineEntry[] => {
  const isActive = !TERMINAL_STAGES.has(investment.stage);
  let currentAssigned = false;

  return INVESTOR_TIMELINE_STEPS.map(({ key, label, stages }) => {
    const reachedAt = stages.map(
      (stage) => investment.stageTimeline.find((entry) => entry.stage === stage)?.at ?? null,
    );

    if (reachedAt.every((at): at is string => at !== null)) {
      return { key, label, state: 'done' as const, at: reachedAt[reachedAt.length - 1] };
    }

    if (isActive && !currentAssigned) {
      currentAssigned = true;
      return { key, label, state: 'current' as const, at: null };
    }

    return { key, label, state: 'upcoming' as const, at: null };
  });
};
